import React from 'react';
import { X, FileText, Image, FileSpreadsheet, File as FileIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FileAttachmentProps {
  file: File;
  uploadProgress?: number;
  uploadError?: string;
  onRemove: () => void;
}

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  file,
  uploadProgress,
  uploadError,
  onRemove,
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    const type = file.type;
    if (type.includes('pdf')) return <FileText className="h-4 w-4" />;
    if (type.includes('image')) return <Image className="h-4 w-4" />;
    if (type.includes('spreadsheet') || type.includes('excel')) return <FileSpreadsheet className="h-4 w-4" />;
    return <FileIcon className="h-4 w-4" />;
  };

  const isUploading = uploadProgress !== undefined && uploadProgress < 100;
  const hasError = !!uploadError;

  return (
    <div
      className={cn(
        "flex items-center gap-2 p-2 rounded-md border",
        hasError ? "border-destructive bg-destructive/10" : "border-border bg-muted",
        isUploading && "opacity-70"
      )}
    >
      <div className="flex-shrink-0">{getFileIcon()}</div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>

          {isUploading && (
            <div className="flex-1 max-w-[100px]">
              <div className="h-1 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {hasError && (
            <p className="text-xs text-destructive">{uploadError}</p>
          )}
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        disabled={isUploading}
        className="h-6 w-6 p-0"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
};
